import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Snackbar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  ContentCopy,

} from '@mui/icons-material';
import { 
  getAllCodes, 
  createCode, 
  updateCode, 
  deleteCode, 
  copyToClipboard,
  getAllRoles,
  getAllPricingPlans
} from './referralUtils';
import { formatDate } from './referralUtils';
import { MESSAGES } from './referralConfig';

const PromoCodeManagement = ({ isChampions = false }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    code_type: 'DISCOUNT',
    discount_type: 'PERCENT',
    discount_amount: '',
    first_purchase_only: true,
    applies_to_roles: '',
    applies_to_plans: '',
    allow_stacking: false,
    auto_apply_if_cookie: true,
    per_user_limit: 1,
    usage_limit_total: '',
    starts_at: '',
    ends_at: '',
    is_active: true,
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState([]);

  // Roles for dropdown: exclude ADMIN (promo codes are for STUDENT, TEACHER, PARENT, CHAMPIONS only)
  const rolesForSelect = useMemo(() => {
    return (roles || []).filter(r => {
      const name = (r.role_name || '').toUpperCase();
      return name && name !== 'ADMIN';
    });
  }, [roles]);

  // Plans for dropdown: when roles are selected, show only plans for those roles (from DB role_id)
  const plansForSelect = useMemo(() => {
    if (!pricingPlans || pricingPlans.length === 0) return [];
    if (selectedRoleIds.length === 0) return pricingPlans;
    return pricingPlans.filter(p => {
      const planRoleId = typeof p.role_id === 'object' ? p.role_id?.id : p.role_id;
      if (planRoleId == null) return true;
      return selectedRoleIds.includes(Number(planRoleId));
    });
  }, [pricingPlans, selectedRoleIds]);

  useEffect(() => {
    loadCodes();
    loadRolesAndPlans();
  }, []);

  const loadCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isChampions) {
        // For champions, show user-specific codes
        // This would need a backend endpoint for user-specific promo codes
        // For now, we'll show a message that this feature is not yet implemented
        setCodes([]);
        setError('User-specific promo codes are not yet available for champions.');
      } else {
        // For admins, show all codes
        const data = await getAllCodes();
        setCodes(data.results || data.codes || []);
      }
    } catch (err) {
      setError('Failed to load promo codes');
      console.error('Error loading codes:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRolesAndPlans = async () => {
    try {
      if (isChampions) {
        // For champions, we don't need to load all roles and plans
        // as they won't be creating admin-level promo codes
        setRoles([]);
        setPricingPlans([]);
        return;
      }
      
      const [rolesData, plansData] = await Promise.all([
        getAllRoles(),
        getAllPricingPlans()
      ]);

      setRoles(rolesData.roles || []);
      setPricingPlans(plansData.plans || []);
    } catch (err) {
      console.error('Error loading roles and plans:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleSelection = (event) => {
    const value = event.target.value;
    const nextIds = Array.isArray(value)
      ? value.filter(v => v !== 'all').map(v => Number(v)).filter(n => !Number.isNaN(n))
      : (value === 'all' ? [] : [Number(value)].filter(n => !Number.isNaN(n)));
    setSelectedRoleIds(nextIds);
    const roleNames = nextIds.length === 0
      ? ''
      : nextIds.map(id => roles.find(r => r.id === id)?.role_name).filter(Boolean).join(',');
    setFormData(prev => ({ ...prev, applies_to_roles: roleNames }));
  };

  const handlePlanSelection = (event) => {
    const value = event.target.value;
    const nextIds = Array.isArray(value)
      ? value.filter(v => v !== 'all').map(v => Number(v)).filter(n => !Number.isNaN(n))
      : (value === 'all' ? [] : [Number(value)].filter(n => !Number.isNaN(n)));
    setSelectedPlanIds(nextIds);
    setFormData(prev => ({
      ...prev,
      applies_to_plans: nextIds.length > 0 ? nextIds.join(',') : '',
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.code.trim()) {
      errors.code = 'Code is required';
    } else if (formData.code.length < 3) {
      errors.code = 'Code must be at least 3 characters';
    } else if (!/^[A-Za-z0-9-_]+$/.test(formData.code)) {
      errors.code = 'Code can only contain letters, numbers, hyphens, and underscores';
    }
    
    if (formData.code_type === 'DISCOUNT') {
      if (!formData.discount_type) {
        errors.discount_type = 'Discount type is required';
      }
      if (!formData.discount_amount || formData.discount_amount <= 0) {
        errors.discount_amount = 'Discount amount is required and must be greater than 0';
      }
      if (formData.discount_type === 'PERCENT' && formData.discount_amount > 100) {
        errors.discount_amount = 'Discount percentage cannot exceed 100%';
      }
    }
    
    if (formData.per_user_limit && formData.per_user_limit < 1) {
      errors.per_user_limit = 'Per user limit must be at least 1';
    }
    
    if (formData.usage_limit_total && formData.usage_limit_total < 1) {
      errors.usage_limit_total = 'Usage limit must be at least 1';
    }
    
    if (formData.starts_at && formData.ends_at && new Date(formData.starts_at) >= new Date(formData.ends_at)) {
      errors.ends_at = 'End date must be after start date';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        applies_to_roles: selectedRoleIds.length === 0 ? '0' : formData.applies_to_roles, // backend expects role names or '0' for all
        applies_to_plans: formData.applies_to_plans || '0', // '0' means all plans
        starts_at: formData.starts_at || null,
        ends_at: formData.ends_at || null,
        usage_limit_total: formData.usage_limit_total || null,
      };
      
      if (editingCode) {
        const codeId = editingCode.id ?? editingCode.pk;
        if (!codeId) {
          setSnackbar({ open: true, message: 'Cannot update: code ID missing.', severity: 'error' });
          return;
        }
        const response = await updateCode(codeId, submitData);
        setCodes(prev => prev.map(c => (c.id ?? c.pk) === codeId ? response : c));
        setSnackbar({
          open: true,
          message: MESSAGES.SUCCESS.CODE_UPDATED,
          severity: 'success'
        });
      } else {
        const response = await createCode(submitData);
        setCodes(prev => [response, ...prev]);
        setSnackbar({
          open: true,
          message: MESSAGES.SUCCESS.CODE_CREATED,
          severity: 'success'
        });
      }
      
      handleCloseDialog();
      resetForm();
    } catch (err) {
      setSnackbar({
        open: true,
        message: editingCode ? MESSAGES.ERROR.CODE_UPDATE_FAILED : MESSAGES.ERROR.CODE_CREATE_FAILED,
        severity: 'error'
      });
      console.error('Error saving code:', err);
    }
  };

  const handleEdit = (code) => {
    const codeId = code?.id ?? code?.pk;
    if (codeId == null) {
      setSnackbar({ open: true, message: 'Cannot edit this code. Refresh the list and try again.', severity: 'warning' });
      return;
    }
    setEditingCode(code);
    
    // Parse applies_to_roles: backend may store role names (e.g. "STUDENT,TEACHER") or legacy IDs
    let roleIds = [];
    if (code.applies_to_roles && code.applies_to_roles !== '0') {
      const segments = code.applies_to_roles.split(',').map(s => s.trim()).filter(Boolean);
      const allNumeric = segments.every(s => /^\d+$/.test(s));
      if (allNumeric) {
        roleIds = segments.map(s => parseInt(s, 10)).filter(id => roles.some(r => r.id === id));
      } else {
        roleIds = segments
          .map(name => {
            const r = roles.find(ro => (ro.role_name || '').toUpperCase() === String(name).toUpperCase());
            return r ? r.id : null;
          })
          .filter(Boolean);
      }
      // Exclude ADMIN so dropdown never shows it as selected
      const adminId = roles.find(r => (r.role_name || '').toUpperCase() === 'ADMIN')?.id;
      if (adminId != null) roleIds = roleIds.filter(id => id !== adminId);
    }
    setSelectedRoleIds(roleIds);
    
    // Parse applies_to_plans: store is comma-separated plan IDs; keep only valid IDs to avoid NaN in display
    let planIds = [];
    if (code.applies_to_plans && code.applies_to_plans !== '0') {
      const validIds = (pricingPlans || []).map(p => p.id);
      planIds = code.applies_to_plans
        .split(',')
        .map(s => parseInt(s.trim(), 10))
        .filter(id => !Number.isNaN(id) && validIds.includes(id));
    }
    setSelectedPlanIds(planIds);
    
    setFormData({
      code: code.code,
      code_type: code.code_type,
      discount_type: code.discount_type || 'PERCENT',
      discount_amount: code.discount_amount || '',
      first_purchase_only: code.first_purchase_only || false,
      applies_to_roles: code.applies_to_roles || '',
      applies_to_plans: code.applies_to_plans || '',
      allow_stacking: code.allow_stacking || false,
      auto_apply_if_cookie: code.auto_apply_if_cookie !== false,
      per_user_limit: code.per_user_limit || 1,
      usage_limit_total: code.usage_limit_total || '',
      starts_at: code.starts_at ? new Date(code.starts_at).toISOString().slice(0, 16) : '',
      ends_at: code.ends_at ? new Date(code.ends_at).toISOString().slice(0, 16) : '',
      is_active: code.is_active !== false,
    });
    setOpenDialog(true);
  };

  const handleDelete = async (code) => {
    const codeId = code?.id ?? code?.pk;
    const codeLabel = (code && typeof code === 'object' && code.code) ? code.code : String(code ?? '');
    if (window.confirm(`Are you sure you want to delete the code "${codeLabel}"?`)) {
      try {
        await deleteCode(codeId);
        setCodes(prev => prev.filter(c => (c?.id ?? c?.pk) !== codeId));
        setSnackbar({
          open: true,
          message: MESSAGES.SUCCESS.CODE_DELETED,
          severity: 'success'
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: MESSAGES.ERROR.CODE_DELETE_FAILED,
          severity: 'error'
        });
        console.error('Error deleting code:', err);
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCode(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      code: '',
      code_type: 'DISCOUNT',
      discount_type: 'PERCENT',
      discount_amount: '',
      first_purchase_only: true,
      applies_to_roles: '',
      applies_to_plans: '',
      allow_stacking: false,
      auto_apply_if_cookie: true,
      per_user_limit: 1,
      usage_limit_total: '',
      starts_at: '',
      ends_at: '',
      is_active: true,
    });
    setFormErrors({});
    setSelectedRoleIds([]);
    setSelectedPlanIds([]);
  };

  const handleCopyCode = async (code) => {
    try {
      await copyToClipboard(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const getStatusChip = (code) => {
    if (!code.is_active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    if (code.ends_at && new Date(code.ends_at) < new Date()) {
      return <Chip label="Expired" color="warning" size="small" />;
    }
    if (code.starts_at && new Date(code.starts_at) > new Date()) {
      return <Chip label="Scheduled" color="info" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  const getTypeChip = (codeType) => {
    return (
      <Chip 
        label={codeType} 
        color={codeType === 'DISCOUNT' ? 'secondary' : 'primary'}
        size="small"
      />
    );
  };

  const formatRolesDisplay = (rolesStr) => {
    if (!rolesStr || rolesStr === '0') return 'All Roles';
    const segments = rolesStr.split(',').map(s => s.trim()).filter(Boolean);
    return segments.map(seg => {
      if (/^\d+$/.test(seg)) {
        const role = roles.find(r => r.id === parseInt(seg, 10));
        return role ? role.role_name : seg;
      }
      const role = roles.find(r => (r.role_name || '').toUpperCase() === seg.toUpperCase());
      return role ? role.role_name : seg;
    }).join(', ');
  };

  const formatPlansDisplay = (plansStr) => {
    if (!plansStr || plansStr === '0') return 'All Plans';
    const planIds = plansStr
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(id => !Number.isNaN(id));
    return planIds.map(id => {
      const plan = pricingPlans.find(p => p.id === id);
      return plan ? (plan.name ?? plan.display_name ?? id) : id;
    }).join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<Refresh />}
            onClick={loadCodes}
            variant="outlined"
          >
            Refresh
          </Button>
          {!isChampions && (
            <Button
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              variant="contained"
            >
              Create Code
            </Button>
          )}
        </Box>
      </Box>

      {/* Create/Edit Code Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingCode ? 'Edit Promo Code' : 'Create New Promo Code'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="code"
                  label="Code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="SAVE20"
                  error={!!formErrors.code}
                  helperText={formErrors.code}
                  required
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Code Type</InputLabel>
                  <Select
                    name="code_type"
                    value={formData.code_type}
                    onChange={handleInputChange}
                    label="Code Type"
                    error={!!formErrors.code_type}
                  >
                    <MenuItem value="REFERRAL">Referral</MenuItem>
                    <MenuItem value="DISCOUNT">Discount</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.code_type === 'DISCOUNT' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        name="discount_type"
                        value={formData.discount_type}
                        onChange={handleInputChange}
                        label="Discount Type"
                        error={!!formErrors.discount_type}
                      >
                        <MenuItem value="PERCENT">Percentage (%)</MenuItem>
                        <MenuItem value="FIXED">Fixed Amount</MenuItem>
                      </Select>
                    </FormControl>
                    {formErrors.discount_type && (
                      <Typography variant="caption" color="error">
                        {formErrors.discount_type}
                      </Typography>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="discount_amount"
                      label="Discount Amount"
                      type="number"
                      value={formData.discount_amount}
                      onChange={handleInputChange}
                      placeholder={formData.discount_type === 'PERCENT' ? '25' : '50'}
                      error={!!formErrors.discount_amount}
                      helperText={formErrors.discount_amount || (formData.discount_type === 'PERCENT' ? 'Enter percentage (0-100)' : 'Enter amount')}
                      inputProps={{
                        min: 0,
                        max: formData.discount_type === 'PERCENT' ? 100 : undefined,
                        step: formData.discount_type === 'PERCENT' ? 1 : 0.01,
                      }}
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="per_user_limit"
                  label="Per User Limit"
                  type="number"
                  value={formData.per_user_limit}
                  onChange={handleInputChange}
                  error={!!formErrors.per_user_limit}
                  helperText={formErrors.per_user_limit || 'How many times each user can use this code'}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="usage_limit_total"
                  label="Total Usage Limit"
                  type="number"
                  value={formData.usage_limit_total}
                  onChange={handleInputChange}
                  error={!!formErrors.usage_limit_total}
                  helperText={formErrors.usage_limit_total || 'Total number of times this code can be used (leave empty for unlimited)'}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="starts_at"
                  label="Start Date"
                  type="datetime-local"
                  value={formData.starts_at}
                  onChange={handleInputChange}
                  error={!!formErrors.starts_at}
                  helperText={formErrors.starts_at || 'When this code becomes active (leave empty for immediate)'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="ends_at"
                  label="End Date"
                  type="datetime-local"
                  value={formData.ends_at}
                  onChange={handleInputChange}
                  error={!!formErrors.ends_at}
                  helperText={formErrors.ends_at || 'When this code expires (leave empty for no expiry)'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Applies to Roles</InputLabel>
                  <Select
                    multiple
                    value={selectedRoleIds}
                    onChange={handleRoleSelection}
                    label="Applies to Roles"
                                         renderValue={(selected) => {
                       if (selected.length === 0) return 'All Roles';
                       return selected.map(id => {
                         const role = rolesForSelect.find(r => r.id === id) || roles.find(r => r.id === id);
                         return role ? role.role_name : id;
                       }).join(', ');
                     }}
                  >
                    <MenuItem value="all">
                      <em>All Roles</em>
                    </MenuItem>
                    {rolesForSelect.map((role) => (
                       <MenuItem key={role.id} value={role.id}>
                         {role.role_name}
                       </MenuItem>
                     ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    Select specific roles or choose "All Roles"
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Applies to Plans</InputLabel>
                  <Select
                    multiple
                    value={selectedPlanIds}
                    onChange={handlePlanSelection}
                    label="Applies to Plans"
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'All Plans';
                      return selected.map(id => {
                        const plan = pricingPlans.find(p => p.id === id);
                        return plan ? (plan.name ?? plan.display_name ?? id) : id;
                      }).join(', ');
                    }}
                  >
                    <MenuItem value="all">
                      <em>All Plans</em>
                    </MenuItem>
                    {plansForSelect.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name ?? plan.display_name ?? plan.id}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    Select specific plans or choose "All Plans"
                  </Typography>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="first_purchase_only"
                          checked={formData.first_purchase_only}
                          onChange={handleInputChange}
                        />
                      }
                      label="First Purchase Only"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="allow_stacking"
                          checked={formData.allow_stacking}
                          onChange={handleInputChange}
                        />
                      }
                      label="Allow Stacking"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="auto_apply_if_cookie"
                          checked={formData.auto_apply_if_cookie}
                          onChange={handleInputChange}
                        />
                      }
                      label="Auto-apply if Cookie"
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleInputChange}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingCode ? 'Update Code' : 'Create Code'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadCodes}>
            Retry
          </Button>
        } sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Codes Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Promo Codes ({codes.length})
          </Typography>
          
          {codes.length === 0 ? (
            <Alert severity="info">
              {MESSAGES.INFO.NO_CODES}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                                 <TableHead>
                   <TableRow>
                     <TableCell>Code</TableCell>
                     <TableCell>Type</TableCell>
                     <TableCell>Discount</TableCell>
                     <TableCell>Usage</TableCell>
                     <TableCell>Valid Period</TableCell>
                     <TableCell>Roles</TableCell>
                     <TableCell>Plans</TableCell>
                     <TableCell>Status</TableCell>
                     <TableCell>Created</TableCell>
                     <TableCell>Actions</TableCell>
                   </TableRow>
                 </TableHead>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code?.id ?? code?.pk ?? code?.code ?? Math.random()}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {(code && typeof code === 'object' ? code.code : code) ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getTypeChip(code?.code_type)}
                      </TableCell>
                      <TableCell>
                        {code.discount_amount ? (
                          <Typography variant="body2">
                            {code.discount_type === 'PERCENT' ? `${code.discount_amount}%` : `$${code.discount_amount}`}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {code.per_user_limit || 'Unlimited'} per user
                          {code.usage_limit_total && (
                            <><br />Max: {code.usage_limit_total}</>
                          )}
                        </Typography>
                      </TableCell>
                                             <TableCell>
                         <Typography variant="body2">
                           {code.starts_at ? formatDate(code.starts_at) : 'No start'}
                           <br />
                           {code.ends_at ? formatDate(code.ends_at) : 'No expiry'}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Typography variant="body2">
                           {formatRolesDisplay(code.applies_to_roles)}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Typography variant="body2">
                           {formatPlansDisplay(code.applies_to_plans)}
                         </Typography>
                       </TableCell>
                       <TableCell>
                         {getStatusChip(code)}
                       </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(code.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Copy code">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyCode((code && typeof code === 'object' ? code.code : code) ?? '')}
                              color="primary"
                            >
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          
                          {!isChampions && (
                            <>
                              <Tooltip title="Edit code">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(code)}
                                  color="primary"
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Delete code">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(code)}
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Success Message */}
      {copied && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {MESSAGES.SUCCESS.COPIED_TO_CLIPBOARD}
        </Alert>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromoCodeManagement;
